import type { CSSProperties } from 'react';
import type { LineItemsTableContent } from '@/blocks/domain/types';
import { filterLineItemsColumnsForAudience, isLineItemsNumericColumn } from '@/blocks/domain/helpers/lineItemsColumnKeys';
import { BORDER_COLORS } from '@/common/presentation/constants/colors';
import { blockFontFamilyCss, measuredFontSizeCss } from '@/document-render/application/blockFontSize';
import { useDocumentAudience } from '@/document-render/presentation/primitives/useDocumentAudience';
import { getViewerLocale } from '@/viewer-locale/application/viewerLocaleStore';
import type { RendererProps } from '../../domain/RendererContract';
import { buildTableColumnWidthPercentages, DEFAULT_LINE_ITEMS_TABLE_COLUMN_WEIGHTS } from '../../domain/buildTableColumnWidthPercentages';
import {
  formatLineItemsCellValue,
  isLineItemsMoneyColumn,
  resolveContextCurrency,
  resolveContextCultureTag
} from '../../domain/formatDocumentMoneyCell';
import { MONEY_CELL_STYLE, MONEY_COLUMN_MIN_WIDTH } from './moneyCellStyles';
import { useQuoteCanvasEditing } from '@/document-render/presentation/editing/quote/QuoteCanvasEditingContext';
import { LineItemsQuoteTable, toQuoteLineItems } from './lineItems/LineItemsQuoteTable';

const TABLE_BODY_CELL_STYLE: CSSProperties = {
  padding: '4px 6px',
  overflowWrap: 'break-word',
  wordBreak: 'normal'
};

function resolveHeaderMinWidth(isMoney: boolean, isCompact: boolean, widthPercent: number | undefined): string | number | undefined {
  if (isMoney) return MONEY_COLUMN_MIN_WIDTH;
  if (isCompact && widthPercent !== undefined) return `${widthPercent}%`;
  return undefined;
}

function resolveColStyle(isCompact: boolean, isMoney: boolean, width: number): CSSProperties {
  if (isCompact) {
    if (isMoney) return { minWidth: MONEY_COLUMN_MIN_WIDTH };
    return { minWidth: `${width}%` };
  }
  if (isMoney) return { width: `${width}%`, minWidth: MONEY_COLUMN_MIN_WIDTH };
  return { width: `${width}%` };
}

export function Header(props: RendererProps<LineItemsTableContent>) {
  const { content, viewport } = props;
  const audience = useDocumentAudience();
  const isCompact = viewport?.compact === true;
  const cols = filterLineItemsColumnsForAudience(content.columns ?? [], audience);
  const columnWidths = buildTableColumnWidthPercentages(
    cols.map((col) => col.key),
    isLineItemsNumericColumn,
    DEFAULT_LINE_ITEMS_TABLE_COLUMN_WEIGHTS
  );

  return (
    <thead>
      <tr>
        {cols.map((col, index) => {
          const isMoney = isLineItemsMoneyColumn(col.key);
          return (
            <th
              key={col.key}
              style={{
                width: isCompact ? undefined : `${columnWidths[index]}%`,
                minWidth: resolveHeaderMinWidth(isMoney, isCompact, columnWidths[index]),
                textAlign: isLineItemsNumericColumn(col.key) ? 'right' : 'left',
                whiteSpace: isMoney || isCompact ? 'nowrap' : undefined,
                overflowWrap: isMoney ? MONEY_CELL_STYLE.overflowWrap : 'break-word',
                wordBreak: 'normal',
                letterSpacing: 'normal',
                ...(isMoney ? { fontVariantNumeric: MONEY_CELL_STYLE.fontVariantNumeric } : {})
              }}
            >
              {col.label}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export function Component(props: RendererProps<LineItemsTableContent>) {
  const quote = useQuoteCanvasEditing();
  const { content, context, style, slice, viewport } = props;
  const audience = useDocumentAudience();
  const isCompact = viewport?.compact === true;
  const viewerLocale = getViewerLocale();
  const currency = resolveContextCurrency(context as Record<string, unknown>, viewerLocale.currency);
  const cultureTag = resolveContextCultureTag(context as Record<string, unknown>, viewerLocale.cultureTag);
  // Quote Builder can provide preview context where these bags are not arrays.
  // Renderer must be defensive: never crash the whole page for preview-only data.
  const allRows = Array.isArray(context.lineItems) ? context.lineItems : [];
  if (quote?.kind === 'lineItems') {
    const quoteRows = toQuoteLineItems(allRows);
    const quoteStart = slice?.startIndex ?? 0;
    const quoteEnd = slice?.endIndex ?? quoteRows.length;
    return (
      <LineItemsQuoteTable
        rows={quoteRows.slice(quoteStart, quoteEnd)}
        allRows={quoteRows}
        editing={quote.editing}
        currency={currency}
        cultureTag={cultureTag}
        showHeader={!slice || slice.startIndex === 0 || slice.isContinuation}
        showAdd={quoteEnd >= quoteRows.length}
        headerFontSize={measuredFontSizeCss(style)}
      />
    );
  }

  // The customer document has no empty header band: a quote with no line items prints no table.
  if (audience === 'customer' && allRows.length === 0) {
    return null;
  }

  const cols = filterLineItemsColumnsForAudience(content.columns ?? [], audience);
  const columnWidths = buildTableColumnWidthPercentages(
    cols.map((col) => col.key),
    isLineItemsNumericColumn,
    DEFAULT_LINE_ITEMS_TABLE_COLUMN_WEIGHTS
  );

  const start = slice?.startIndex ?? 0;
  const end = slice?.endIndex ?? allRows.length;
  const rows = allRows.slice(start, end);

  const shouldShowHeader = !slice || slice.startIndex === 0 || slice.isContinuation;

  const rowHeight = 22;
  const minHeight = rows.length * rowHeight + (shouldShowHeader ? rowHeight : 0);

  return (
    <section
      aria-label="block-line-items-table"
      style={{
        minHeight,
        paddingTop: style.paddingY,
        paddingBottom: style.paddingY,
        color: style.textColor,
        background: style.backgroundColor,
        fontSize: isCompact ? Math.max(10, (style.fontSize ?? 14) - 1) : style.fontSize,
        fontWeight: style.fontWeight,
        fontFamily: blockFontFamilyCss(style)
      }}
    >
      {content.title ? <div style={{ fontWeight: 700, marginBottom: 6 }}>{content.title}</div> : null}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: isCompact ? 'auto' : 'fixed'
        }}
      >
        <colgroup>
          {columnWidths.map((width, index) => {
            const key = cols[index]!.key;
            const isMoney = isLineItemsMoneyColumn(key);
            return <col key={key} style={resolveColStyle(isCompact, isMoney, width)} />;
          })}
        </colgroup>
        {shouldShowHeader ? <Header {...props} /> : null}
        <tbody>
          {rows.map((row, idx) => (
            <tr key={slice ? `${slice.startIndex + idx}` : `${idx}`} style={{ borderBottom: `1px solid ${BORDER_COLORS.LIGHT}` }}>
              {cols.map((col) => {
                const value = (row as Record<string, unknown>)[col.key];
                const text = formatLineItemsCellValue(value, col.key, currency, cultureTag);
                const isMoney = isLineItemsMoneyColumn(col.key);
                return (
                  <td
                    key={col.key}
                    style={{
                      ...TABLE_BODY_CELL_STYLE,
                      ...(isMoney ? MONEY_CELL_STYLE : {}),
                      minWidth: isMoney ? MONEY_COLUMN_MIN_WIDTH : undefined,
                      textAlign: isLineItemsNumericColumn(col.key) ? 'right' : 'left'
                    }}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {content.showRateScheduleNote ? (
        <div style={{ fontSize: 11, fontStyle: 'italic', marginTop: 8 }}>Rates reflect standard time.</div>
      ) : null}
    </section>
  );
}

export const renderer = {
  Component,
  flowBehavior: 'splittable' as const,
  Header
};

export type { LineItemsTableContent };
